import { AppIcon } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";

dayjs.extend(relativeTime);

const MIN_WIDTH = 340;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 460;
const PANEL_INSET = 12;
const PANEL_STATE_KEY = "tfcode-panel-state";

function loadPanelState() {
  try {
    const raw = localStorage.getItem(PANEL_STATE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return null;
}

function savePanelState(state) {
  try {
    localStorage.setItem(PANEL_STATE_KEY, JSON.stringify(state));
  } catch {}
}

// Resizable + collapsible overlay side panel hosting the embedded tfcode view.
// Sits ON TOP of the app (position: fixed), so the underlying app stays visible
// and the existing Code tab is never touched.
const TfcodeSidePanel = ({ url, iframeRef, onClose, onSignIn }) => {
  const { currentTheme, mode } = AppTheme.useTheme();
  const { i18n, t } = useTranslation();
  const saved = loadPanelState();
  const [width, setWidth] = useState(saved?.width ?? DEFAULT_WIDTH);
  const [collapsed, setCollapsed] = useState(saved?.collapsed ?? false);
  const [snap, setSnap] = useState(saved?.snap ?? "top");
  const [dragY, setDragY] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [showSessions, setShowSessions] = useState(false);
  const dragging = useRef(false);
  const tabRef = useRef(null);
  const tabDrag = useRef(null);
  const timeoutRef = useRef(null);

  const srcOrigin = (() => {
    try {
      return new URL(url).origin;
    } catch {
      return "*";
    }
  })();

  const src = (() => {
    if (!url) return null;
    const stripped = String(url)
      .replace(/[?&]theme=[^&]*/g, "")
      .replace(/[?&]lang=[^&]*/g, "")
      .replace(/[?&]bg=[^&]*/g, "")
      .replace(/[?&]$/, "");
    const sep = stripped.includes("?") ? "&" : "?";
    const lang = i18n?.language || "en";
    const bgParam =
      mode === "dark"
        ? `&bg=${encodeURIComponent(
            currentTheme?.theme?.backgroundColor || "#0c0c0c",
          )}`
        : "";
    return `${stripped}${sep}theme=${mode}&lang=${lang}${bgParam}`;
  })();

  // Persist width/collapsed/snap to localStorage.
  useEffect(() => {
    savePanelState({ width, collapsed, snap });
  }, [width, collapsed, snap]);

  // Escape closes the sessions dropdown first, then the panel.
  useEffect(() => {
    if (!url) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (showSessions) {
        setShowSessions(false);
        return;
      }
      if (!collapsed) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [url, collapsed, showSessions, onClose]);

  // Drag-to-resize on the left edge.
  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const next = Math.min(
        MAX_WIDTH,
        Math.max(MIN_WIDTH, window.innerWidth - PANEL_INSET - e.clientX),
      );
      setWidth(next);
    };
    const onUp = () => {
      dragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  // Same theme CSS the Code tab injects into the tfcode iframe
  // (mirrors buildTfcodeThemeCss in MainViewScreen).
  const buildTfcodeThemeCss = useCallback(() => {
    const t = currentTheme?.theme;
    if (!t) return "";
    const isDark = mode === "dark";
    const bg = t.backgroundColor || (isDark ? "#0c0c0c" : "#ffffff");
    const border2 = t.border_color || (isDark ? "#232323" : "#E8E8E8");
    const outline = t.app_outline_color || border2;
    const primary = t.primary || "#1db8c6";
    const orange = t.orange || "#f97316";
    const red = t.red || "#ef4444";

    const bodyBg = `html[data-theme], html[data-theme] body { background-color: ${bg} !important; }`;
    const surfaceBase = isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)";
    const surfaceInset = isDark ? "#1a1a1a" : "#EFEFEF";
    const surfaceRaised = isDark ? "#272727" : "#EFEFEF";
    const surfaceRaisedHover = isDark ? "#333" : "#e0e0e0";
    const surfaceStrongerNonAlpha = isDark ? "#1c1c1c" : "#EFEFEF";
    const surfaceFloat = isDark ? "#1a1a1a" : "#ffffff";
    const bgDark = isDark ? "#1a1a1a" : bg;
    const bgAll = `:root {
      --background-base: ${bgDark};
      --background-weak: ${bgDark};
      --background-strong: ${bgDark};
      --background-stronger: ${bgDark};
      --surface-base: ${surfaceBase};
      --surface-inset-base: ${surfaceInset};
      --surface-raised-base: ${surfaceRaised};
      --surface-raised-base-hover: ${surfaceRaisedHover};
      --surface-raised-base-active: ${surfaceRaisedHover};
      --surface-raised-strong: ${surfaceRaisedHover};
      --surface-raised-stronger: ${surfaceInset};
      --surface-raised-stronger-non-alpha: ${surfaceStrongerNonAlpha};
      --surface-float-base: ${surfaceFloat};
    }`;
    const tooltipFix = `[data-component="tooltip"] { background: ${
      isDark ? "#1a1a1a" : "#ffffff"
    } !important; color: ${
      isDark ? "#ffffff" : "#000000"
    } !important; border: 1px solid ${border2} !important; }`;
    const iconOrange = `[data-component="icon-button"][data-variant="ghost"]:active:not(:disabled):not([data-icon="settings"]):not([data-icon="sliders"]) [data-slot="icon-svg"], [data-component="icon-button"][data-variant="ghost"][aria-pressed="true"]:not([data-icon="settings"]):not([data-icon="sliders"]) [data-slot="icon-svg"], [data-component="icon-button"][data-variant="ghost"][aria-selected="true"]:not([data-icon="settings"]):not([data-icon="sliders"]) [data-slot="icon-svg"], [data-component="icon-button"][data-variant="ghost"][data-state="active"]:not([data-icon="settings"]):not([data-icon="sliders"]) [data-slot="icon-svg"], .group\\/terminal-toggle[aria-expanded="true"] [data-slot="icon-svg"], .group\\/review-toggle[aria-expanded="true"] [data-slot="icon-svg"], [aria-expanded="true"].titlebar-icon [data-slot="icon-svg"], [aria-current="page"].titlebar-icon [data-slot="icon-svg"], .text-icon-strong [data-slot="icon-svg"] { color: ${orange} !important; }`;
    const noBg = `[data-component="icon-button"][data-variant="ghost"]:hover:not(:disabled), [data-component="icon-button"][data-variant="ghost"]:focus-visible:not(:disabled), [data-component="icon-button"][data-variant="ghost"]:active:not(:disabled), [data-component="icon-button"][data-variant="ghost"][aria-pressed="true"], [data-component="icon-button"][data-variant="ghost"][aria-selected="true"], [data-component="icon-button"][data-variant="ghost"][data-state="active"], .group\\/terminal-toggle, .group\\/review-toggle, .titlebar-icon { background-color: transparent !important; }`;
    const promptInputBg = isDark ? "#272727" : "#EFEFEF";
    const promptInputFix = `[data-action="prompt-submit"] [data-component="icon"] { width: 18px !important; height: 18px !important; } [data-component="icon-button"][data-icon="send"] [data-slot="icon-svg"] { color: ${primary} !important; } [data-component="icon-button"][data-icon="stop"] [data-slot="icon-svg"], [data-component="icon-button"][data-icon="stopGeneration"] [data-slot="icon-svg"] { color: ${red} !important; } [data-component="prompt-input"] { background-color: ${promptInputBg} !important; }`;
    const menuBgFix = `[data-component="dialog"] [data-slot="dialog-content"], [data-component="popover-content"], [data-component="dropdown-menu-content"], [data-component="dropdown-menu-sub-content"] { background: ${
      isDark ? "var(--background-base)" : "#EFEFEF"
    } !important; }`;
    const fileTreeIconFix = `[data-component="filetree"] .group:hover [data-component="file-icon"] { color: ${primary} !important; }`;
    const textColor = t.font_color || (isDark ? "#ffffff" : "#000000");
    const borderFix = `html[data-theme] { --border-base: ${outline} !important; --border-weak-base: ${outline} !important; --border-weaker-base: ${outline} !important; }`;
    const textFix = `html[data-theme] { --text-base: ${textColor} !important; --text-strong: ${textColor} !important; --text-weak: ${textColor} !important; }`;
    const reviewIconFix = `html[data-theme] { --icon-interactive-base: ${primary} !important; --interactive: ${primary} !important; }`;
    const diffSelectionFix = `[data-line][data-selected-line] { background-color: ${primary}33 !important; box-shadow: inset 2px 0 0 ${primary} !important; } [data-column-number][data-selected-line] { background-color: ${primary}4d !important; color: #fff !important; }`;
    const inlineInputFix = `[data-component="inline-input"]:focus-within, [data-component="inline-input"] textarea:focus, [data-component="inline-input"] input:focus, [data-component="inline-input"] [contenteditable]:focus { box-shadow: none !important; outline: none !important; border-color: transparent !important; }`;
    const topbarBg = t.backgroundColorAppBorders || bg;
    const layoutFix = `[data-component="sidebar-nav-desktop"], [data-component="sidebar-nav-desktop"] > div, [data-component="sidebar-nav-mobile"], [data-component="sidebar-nav-mobile"] > div, nav[data-component="sidebar-nav-desktop"] .bg-background-base, nav[data-component="sidebar-nav-desktop"] .bg-background-stronger, nav[data-component="sidebar-nav-mobile"] .bg-background-base { background-color: ${topbarBg} !important; } header.h-10.shrink-0 { background-color: ${topbarBg} !important; }`;
    return `${bgAll} ${bodyBg} ${tooltipFix} ${iconOrange} ${noBg} ${promptInputFix} ${menuBgFix} ${fileTreeIconFix} ${borderFix} ${textFix} ${reviewIconFix} ${diffSelectionFix} ${inlineInputFix} ${layoutFix}`;
  }, [currentTheme, mode]);

  const sendEmbedConfig = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow || !src) return;
    const targetOrigin = (() => {
      try {
        return new URL(src).origin;
      } catch {
        return "*";
      }
    })();
    iframe.contentWindow.postMessage(
      {
        v: 1,
        type: "tfcode-inject-css",
        css: buildTfcodeThemeCss(),
        mode,
      },
      targetOrigin,
    );
  }, [mode, src, iframeRef, buildTfcodeThemeCss]);

  // Forward theme changes to the embedded iframe so it stays in sync.
  useEffect(() => {
    sendEmbedConfig();
  }, [sendEmbedConfig]);

  // Listen for the embed's session index (pushed on mount, directory change and
  // every 15s) and its state reports (current session id).
  useEffect(() => {
    const onMessage = (event) => {
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow || event.source !== iframe.contentWindow)
        return;
      const data = event.data;
      if (!data || typeof data !== "object") return;
      if (data.type === "tfcode-sessions" && Array.isArray(data.sessions)) {
        setSessions(data.sessions);
        return;
      }
      if (data.type === "tfcode-state") {
        setActiveSessionId(data.sessionID ?? null);
        return;
      }
      // The embed asks the host to sign in: the host runs its (already
      // working) Cognito flow and answers with a "tfcode-credentials"
      // message the embed bridge applies to the sidecar.
      if (data.type === "tfcode-signin") {
        onSignIn?.();
        return;
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [iframeRef, onSignIn]);

  // Ask the embed to open an existing session, or start a new one when
  // sessionID is omitted (the embed navigates internally — no reload).
  const openSession = useCallback(
    (sessionID) => {
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow) return;
      iframe.contentWindow.postMessage(
        { v: 1, type: "tfcode-session", sessionID },
        srcOrigin,
      );
      setShowSessions(false);
    },
    [iframeRef, srcOrigin],
  );

  // Handle iframe load/error with a timeout so we surface a stuck state.
  const onIframeLoad = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setLoading(false);
    setError(null);
    sendEmbedConfig();
  }, [sendEmbedConfig]);

  const onIframeError = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setLoading(false);
    setError("Failed to load the assistant.");
  }, []);

  useEffect(() => {
    if (!url) return;
    setLoading(true);
    setError(null);
    timeoutRef.current = setTimeout(() => {
      setLoading(false);
      setError("The assistant is taking longer than expected to load.");
    }, 20000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [url]);

  // Drag the collapsed tab vertically; snap to top / middle / bottom on release.
  const onTabMouseDown = useCallback((e) => {
    e.preventDefault();
    const rect = tabRef.current?.getBoundingClientRect();
    tabDrag.current = {
      startY: e.clientY,
      startTop: rect?.top ?? 0,
      height: rect?.height ?? 120,
      moved: false,
    };
    const onMove = (ev) => {
      const d = tabDrag.current;
      if (!d) return;
      if (Math.abs(ev.clientY - d.startY) > 4) d.moved = true;
      if (d.moved) {
        const next = Math.min(
          window.innerHeight - d.height,
          Math.max(0, d.startTop + (ev.clientY - d.startY)),
        );
        setDragY(next);
      }
    };
    const onUp = (ev) => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const d = tabDrag.current;
      tabDrag.current = null;
      setDragY(null);
      if (!d) return;
      if (!d.moved) {
        setCollapsed(false);
        return;
      }
      const third = window.innerHeight / 3;
      const center = ev.clientY;
      setSnap(
        center < third ? "top" : center < third * 2 ? "middle" : "bottom",
      );
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  if (!url) return null;

  const bg =
    currentTheme?.theme?.backgroundColor ||
    (mode === "dark" ? "#0c0c0c" : "#ffffff");
  const border =
    currentTheme?.theme?.app_outline_color ||
    currentTheme?.theme?.border_color ||
    (mode === "dark" ? "#232323" : "#E8E8E8");
  const text =
    currentTheme?.theme?.font_color ||
    (mode === "dark" ? "#ffffff" : "#000000");
  const primary = currentTheme?.theme?.primary || "#1db8c6";

  // Responsive: on narrow viewports switch to full-width bottom sheet style.
  // On desktop the panel is a floating rounded card inset from the viewport
  // edges — premium popover feel, not a full-height dock.
  const isNarrow = typeof window !== "undefined" && window.innerWidth < 640;
  const panelWidth = isNarrow ? "100%" : width;
  const panelHeight = isNarrow ? "80%" : undefined;
  const panelTop = isNarrow ? "auto" : PANEL_INSET;
  const panelBottom = isNarrow ? 0 : PANEL_INSET;
  const panelRight = isNarrow ? 0 : PANEL_INSET;
  const panelLeft = isNarrow ? 0 : "auto";
  const panelBorderRadius = isNarrow ? "12px 12px 0 0" : 14;
  const panelShadow =
    mode === "dark"
      ? "0 16px 40px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.35)"
      : "0 16px 40px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)";

  // Collapsed tab. The panel itself stays MOUNTED (hidden with display:none)
  // so the iframe — and the active tfcode session inside it — is not destroyed
  // on minimise.
  const snapStyle =
    dragY != null
      ? { top: dragY }
      : snap === "middle"
        ? { top: "50%", transform: "translateY(-50%)" }
        : snap === "bottom"
          ? { bottom: 16 }
          : { top: 80 };
  const collapsedTab = (
    <div
      ref={tabRef}
      onMouseDown={onTabMouseDown}
      data-tfcode-collapsed-tab
      style={{
        position: "fixed",
        right: 0,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
        padding: "12px 0",
        width: 44,
        backgroundColor: primary,
        border: `1px solid ${border}`,
        borderRight: "none",
        borderTopLeftRadius: 10,
        borderBottomLeftRadius: 10,
        cursor: "grab",
        userSelect: "none",
        boxShadow:
          mode === "dark"
            ? "0 4px 14px rgba(0,0,0,0.40)"
            : "0 4px 14px rgba(0,0,0,0.12)",
        ...snapStyle,
      }}
    >
      <AppIcon icon={AppAssets.icons.arrow_left} color="#fff" size={14} />
      <span
        style={{
          writingMode: "vertical-rl",
          color: "#fff",
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 1.5,
          fontFamily: "normalFont",
        }}
      >
        {t("topbar.askTfEngineer", { defaultValue: "Ask TF Engineer" })}
      </span>
    </div>
  );

  return (
    <>
      {collapsed && collapsedTab}
      <View
        style={[
          styles.panel,
          {
            width: panelWidth,
            height: panelHeight,
            top: panelTop,
            bottom: panelBottom,
            right: panelRight,
            left: panelLeft,
            borderRadius: panelBorderRadius,
            backgroundColor: bg,
            borderColor: border,
            borderWidth: 1,
            boxShadow: panelShadow,
            display: collapsed ? "none" : "flex",
          },
        ]}
        data-tfcode-panel
      >
        {/* Resize handle on the left edge (desktop only) */}
        {!isNarrow && (
          <View
            style={styles.resizeHandle}
            onMouseDown={(e) => {
              e.preventDefault();
              dragging.current = true;
              document.body.style.cursor = "col-resize";
              document.body.style.userSelect = "none";
            }}
          >
            <View
              style={{
                width: 4,
                height: 40,
                borderRadius: 2,
                backgroundColor: border,
              }}
            />
          </View>
        )}

        {/* Header */}
        <View style={[styles.header, { borderBottomColor: border }]}>
          <Text
            numberOfLines={1}
            style={{ color: text, fontSize: 13, fontWeight: "700", flex: 1 }}
          >
            {t("topbar.askTfEngineer", {
              defaultValue: "Ask TF Engineer",
            })}
          </Text>
          <TouchableOpacity
            onPress={() => setShowSessions((v) => !v)}
            style={styles.headerBtn}
            activeOpacity={0.6}
          >
            <AppIcon
              icon={AppAssets.icons.history}
              color={showSessions ? primary : text}
              size={14}
              wrapperStyle={{ opacity: showSessions ? 1 : 0.8 }}
            />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => openSession(undefined)}
            style={styles.headerBtn}
            activeOpacity={0.6}
          >
            <AppIcon
              icon={AppAssets.icons.plus}
              color={text}
              size={14}
              wrapperStyle={{ opacity: 0.8 }}
            />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => setCollapsed(true)}
            style={styles.headerBtn}
            activeOpacity={0.6}
          >
            <AppIcon
              icon={AppAssets.icons.arrow_right}
              color={text}
              size={14}
              wrapperStyle={{ opacity: 0.8 }}
            />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={onClose}
            style={styles.headerBtn}
            activeOpacity={0.6}
          >
            <AppIcon icon={AppAssets.icons.close} color={text} size={14} />
          </TouchableOpacity>
        </View>

        {/* Sessions dropdown — managed entirely by the panel via the embed bridge */}
        {showSessions && (
          <div
            data-tfcode-sessions
            style={{
              position: "absolute",
              top: 38,
              left: 0,
              right: 0,
              maxHeight: "70%",
              overflowY: "auto",
              backgroundColor: bg,
              borderBottom: `1px solid ${border}`,
              zIndex: 20,
              boxShadow:
                mode === "dark"
                  ? "0 8px 20px rgba(0,0,0,0.35)"
                  : "0 8px 20px rgba(0,0,0,0.08)",
              fontFamily: "normalFont",
            }}
          >
            <button
              type="button"
              onClick={() => openSession(undefined)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "10px 14px",
                background: "none",
                border: "none",
                borderBottom: `1px solid ${border}`,
                color: primary,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                textAlign: "left",
                fontFamily: "inherit",
              }}
            >
              {t("tfcodePanel.newSession", { defaultValue: "New session" })}
            </button>
            {sessions.length === 0 ? (
              <div
                style={{
                  padding: "14px",
                  color: text,
                  opacity: 0.5,
                  fontSize: 12,
                }}
              >
                {t("tfcodePanel.noSessions", {
                  defaultValue: "No sessions yet",
                })}
              </div>
            ) : (
              sessions
                .slice()
                .sort((a, b) => (b.updated || 0) - (a.updated || 0))
                .map((s) => {
                  const isActive = s.id === activeSessionId;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => openSession(s.id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        width: "100%",
                        padding: "9px 14px",
                        background: isActive
                          ? mode === "dark"
                            ? "rgba(255,255,255,0.06)"
                            : "rgba(0,0,0,0.04)"
                          : "none",
                        border: "none",
                        color: text,
                        fontSize: 12,
                        cursor: "pointer",
                        textAlign: "left",
                        fontFamily: "inherit",
                      }}
                    >
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontWeight: isActive ? 700 : 400,
                        }}
                      >
                        {s.title || "Untitled"}
                      </span>
                      <span
                        style={{
                          opacity: 0.5,
                          fontSize: 11,
                          flexShrink: 0,
                        }}
                      >
                        {s.updated ? dayjs(s.updated).fromNow() : ""}
                      </span>
                      {isActive && (
                        <AppIcon
                          icon={AppAssets.icons.tick}
                          color={primary}
                          size={11}
                        />
                      )}
                    </button>
                  );
                })
            )}
          </div>
        )}

        {/* Iframe with loading / error overlay */}
        <View style={{ flex: 1, position: "relative" }}>
          {loading && (
            <View
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: bg,
              }}
            >
              <ActivityIndicator color={primary} size="large" />
              <Text
                style={{
                  color: text,
                  marginTop: 12,
                  fontSize: 13,
                  opacity: 0.7,
                }}
              >
                {t("code.loadingCode", { defaultValue: "Loading assistant…" })}
              </Text>
            </View>
          )}
          {error && !loading && (
            <View
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: bg,
                padding: 24,
              }}
            >
              <AppIcon
                icon={AppAssets.icons.close}
                color={currentTheme?.theme?.red || "#ef4444"}
                size={24}
              />
              <Text
                style={{
                  color: text,
                  marginTop: 12,
                  fontSize: 14,
                  textAlign: "center",
                }}
              >
                {error}
              </Text>
              <TouchableOpacity
                onPress={() => {
                  setError(null);
                  setLoading(true);
                  const iframe = iframeRef.current;
                  if (iframe) iframe.src = iframe.src;
                }}
                style={{ marginTop: 16 }}
              >
                <Text
                  style={{ color: primary, fontSize: 13, fontWeight: "700" }}
                >
                  {t("common.retry", { defaultValue: "Retry" })}
                </Text>
              </TouchableOpacity>
            </View>
          )}
          <iframe
            ref={iframeRef}
            src={src}
            title="tfcode-assistant"
            allow="clipboard-write; microphone; camera"
            onLoad={onIframeLoad}
            onError={onIframeError}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              backgroundColor: bg,
            }}
          />
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  panel: {
    position: "fixed",
    zIndex: 1000,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  resizeHandle: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 10,
    zIndex: 10,
    cursor: "col-resize",
    alignItems: "center",
    justifyContent: "center",
  },
  header: {
    height: 38,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    flexShrink: 0,
  },
  headerBtn: {
    marginLeft: 10,
    padding: 4,
  },
});

export default TfcodeSidePanel;
